import AddIcon from '@mui/icons-material/Add';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';
import { COOKIE_FEATURES_OVERRIDE } from '@shared/constants';
import React from 'react';
import { Button } from '~/components/Button';
import { getCookie } from '~/scripts/cookie';
const COOKIE_EXPIRATION_DAYS = 365;

type FeatureOverrides = Record<string, { enabled: boolean }>;

interface FeatureRow {
  id: number;
  name: string;
  enabled: boolean;
}

let nextRowId = 0;

function toRows(features: FeatureOverrides): FeatureRow[] {
  return Object.entries(features).map(([name, value]) => ({
    id: nextRowId++,
    name,
    enabled: !!value?.enabled,
  }));
}

function toFeatures(rows: FeatureRow[]): FeatureOverrides {
  const features: FeatureOverrides = {};
  rows.forEach((row) => {
    features[row.name] = { enabled: row.enabled };
  });
  return features;
}

function getCookieDomain(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.hostname === 'localhost'
    ? 'localhost'
    : '.nerdydata.com';
}

function saveFeaturesCookie(features: FeatureOverrides): void {
  if (typeof document === 'undefined') {
    return;
  }
  const expiration = new Date();
  expiration.setDate(expiration.getDate() + COOKIE_EXPIRATION_DAYS);
  const secure = location.protocol === 'https:' ? 'Secure;' : '';
  const sameSite = location.protocol === 'https:' ? 'None' : 'Lax';
  document.cookie = `${COOKIE_FEATURES_OVERRIDE}=${encodeURIComponent(
    JSON.stringify(features)
  )}; path=/; domain=${getCookieDomain()}; SameSite=${sameSite}; ${secure} expires=${expiration.toUTCString()};`;
}

function parseFeaturesCookie(): FeatureOverrides {
  const raw = getCookie(COOKIE_FEATURES_OVERRIDE);
  if (!raw) {
    return {};
  }
  try {
    return JSON.parse(raw) as FeatureOverrides;
  } catch {
    return {};
  }
}

interface FeatureOverridesModalProps {
  open: boolean;
  onClose: () => void;
}

export const FeatureOverridesModal = ({
  open,
  onClose,
}: FeatureOverridesModalProps) => {
  const [rows, setRows] = React.useState<FeatureRow[]>([]);

  React.useEffect(() => {
    if (open) {
      setRows(toRows(parseFeaturesCookie()));
    }
  }, [open]);

  const updateRows = (next: FeatureRow[]) => {
    setRows(next);
    saveFeaturesCookie(toFeatures(next));
  };

  const onToggleEnabled = (id: number, enabled: boolean) => {
    updateRows(rows.map((row) => (row.id === id ? { ...row, enabled } : row)));
  };

  const onRenameFeature = (id: number, name: string) => {
    updateRows(rows.map((row) => (row.id === id ? { ...row, name } : row)));
  };

  const onAddFeature = () => {
    const existingNames = new Set(rows.map((row) => row.name));
    let name = 'new_feature';
    let suffix = 1;
    while (existingNames.has(name)) {
      name = `new_feature_${suffix}`;
      suffix += 1;
    }
    updateRows([...rows, { id: nextRowId++, name, enabled: false }]);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Feature Overrides</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {rows.map((row) => {
            return (
              <Stack
                key={row.id}
                direction="row"
                spacing={2}
                alignItems="center">
                <TextField
                  size="small"
                  fullWidth
                  value={row.name}
                  onChange={(event) =>
                    onRenameFeature(row.id, event.target.value)
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={row.enabled}
                      onChange={(event) =>
                        onToggleEnabled(row.id, event.target.checked)
                      }
                    />
                  }
                  label="Enabled"
                />
              </Stack>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<AddIcon />} onClick={onAddFeature}>
          New Feature
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
